/**
 * Created by eliorc on 3/10/15.
 */

/**
 * Documentation: TasksManager
 * ------------------------------
 *
 * this is a simple task manager that can take 'Task' Objects and run them in a queue with several options.
 * -- support Prioritizing & Dynamic Prioritizing.
 * -- support Batch Processing.
 *
 * Public Api:
 * ----------
 *   Methods:
 *   *******
 *      start            : start the manager - all items in the queue will start to process according to the configured queue slots and the prioritizing.
 *      stop             : stop the 'next' run of the queue. (see limitations)
 *      addTask(task)    : take a task and add it for processing.
 *      createTask       : a factory function for the tasks. (see below 'Task Factory' & createTask function documentation.)
 *
 * Task Factory:
 * --------------------------
 *  the task factory wrap an 'Action' with meta data necessary for the task manager eg: priority, timeout and so on,
 *  it's also allow a state management and event binding for the different states.
 *
 *  ******************************************************************************************************************************************
 *  -M- task                : the function to run.
 *  -O- timeout             : the timeout for async task - default from config if not supplied.
 *  -O- priority            : the priority of the task, this will determined the location of the task in the queue.
 *                          : NOTICE: priority can be change if needed, the run loop for the queue will sort the queue before running the next task.
 *                          : ------
 *  -O- taskStartedCallback : will be called when the task is started
 *  -O- taskErrorCallback   : will be called if an error occur in the task process - will run before the taskEndedCallback.
 *  -O- taskEndedCallback   : will be called when the task is done.
 *
 *  M - Mandatory
 *  O - Optional
 *  ******************************************************************************************************************************************
 *
 * The Task Object:
 * ---------------
 * the task is based on 'Action' - atomic logic unit function that contain a callback to inform the task that the action is done.
 * when the logic flow is finished - there is a need to invoke the callback.
 *   example:
 *   var asyncTask = function(doneCallback){
 *
 *     [Sync Logic Flow]
 *
 *     setTimeout(function(){
 *       [A-Sync Logic Flow]
 *
 *       // Calling the Task Callback - indicating the DONE state.
 *       // if we have an error, we will attach it to the done to indicate an error passing to the Task Object.
 *           doneCallback(err);
 *       });
 *   }
 *
 * Error Handling:
 * -----------------
 * the task manager can catch error on the Sync Logic Flow and automatically raise it on the error event.
 * but async error cannot be cached and need to be pass along to the done callback.
 *
 * Limitations:
 * -----------
 * - stopping a task that already started is not possible.
 *
 * troubleshooting:
 * ---------------
 * ensure you always call the done callback when finishing the action flow - otherwise the timeout error will take over even if the task is successfully completed.
 *
 */


/*
 *  TODO's:
 *  √1. forceStart: override the next method and start processing of task.
 *  2. add event for Task: onStateChange.
 *
 * */


define(function (require) {

    var WebAgentLogger = require('web-agent-assets-js/WebAgentLogger');

    var TASK_ID_COUNTER = 0;
    var QUEUE_STATES = { IDLE: 'IDLE', PROCESSING: 'PROCESSING', STOPPED: 'STOPPED'  };
    var TASK_STATES = { PENDING: 'PENDING', RUNNING: 'RUNNING', ERROR: 'ERROR', DONE: 'DONE'};
    var DEFAULT_PRIORITY = 1;
    var DEFAULT_MAX_PARALLEL_TASKS = 2;
    var DEFAULT_TASK_TIMEOUT_IN_MS = 1000 * 15;
    var DEFAULT_AUTO_START = true;
    var TASK_TIMEOUT_ERROR = new Error('Task Timeout');


    /**
     * Private Class
     * *************
     * this is a Task Instance, it can only be created by the TaskManager Create function.
     * @constructor
     */
    function Task(options) {
        // public:
        this.id = ++TASK_ID_COUNTER;
        this.priority = options.priority;
        this.timeout = options.timeout || DEFAULT_TASK_TIMEOUT_IN_MS;
        this.state = TASK_STATES.PENDING;
        this.action = options.action;
        this.error = null;

        // private:
        var callbacks = options.callbacks;
        this.taskStatistics = {
            startTime: null,
            endTime : null,
            runTime: null
        };


        // private functions:
        // ------------------
        function hasCallback(name) {
            return (callbacks && callbacks.hasOwnProperty(name) && typeof callbacks[name] === 'function');
        }

        function fireCallback(name) {
            // extracting the arguments from the function call (removing the name)
            var args = Array.prototype.slice.call(arguments, 1);

            // injecting the 'this' context inside all of the callbacks to indicate the task.
            Array.prototype.splice.call(args, 0, 0, this);

            if (hasCallback.call(this, name)) {
                var cb = callbacks[name];
                cb.apply(null, args);
            }
        }

        function setState(newState) {
            if (this.state !== TASK_STATES.ERROR) {
                if (!TASK_STATES.hasOwnProperty(newState)) {
                    throw new Error('Invalid Task State');
                }
                this.state = newState;
                fireCallback('stateChange', this.state);
            }
        }

        function setError(err) {
            setState(TASK_STATES.ERROR);
            this.error = err;
            fireCallback.call(this, 'error', this.error);
        }

        // Public API:
        // ----------
        this.start = function (doneCallback) {

            this.taskStatistics.startTime = Date.now();

            var isDone = false;
            var timeoutPointer = setTimeout(taskTimeOut.bind(this), this.timeout);

            fireCallback.call(this, 'start');
            setState.call(this, TASK_STATES.RUNNING);

            try {
                this.action(taskDone.bind(this));
            }
            catch (ex) {
                // NOTICE: we can only catch 'Sync' Errors, the A-Sync Errors need to be
                // handled by the Action and send the done callback with the Error.
                taskDone.call(this, ex);
            }

            function taskTimeOut() {
                setError.call(this, TASK_TIMEOUT_ERROR);
                taskDone.call(this);
            }

            function taskDone(err) {

                if (isDone) {
                    return;
                }

                // updating the statistics
                this.taskStatistics.endTime = Date.now();
                this.taskStatistics.runTime = this.taskStatistics.endTime - this.taskStatistics.startTime;

                if (err) {
                    setError.call(this, err);
                }

                setState.call(this, TASK_STATES.DONE);
                fireCallback.call(this, 'end', this.error);
                clearTimeout(timeoutPointer);

                isDone = true;
                doneCallback(this, this.error);

            }
        };
    }

    function TasksManager(options) {
        this.logger = WebAgentLogger.getLogger('TasksManager');

        this.state = QUEUE_STATES.STOPPED;
        this.queue = [];
        this.defaultPriority = (options && options.defaultPriority) || DEFAULT_PRIORITY;
        this.maxParallelTasks = (options && options.maxParallelTasks) || DEFAULT_MAX_PARALLEL_TASKS;
        this.taskTimeout = (options && options.taskTimeout) || DEFAULT_TASK_TIMEOUT_IN_MS;
        this.autoStart = !!((options && options.autoStart) || DEFAULT_AUTO_START);
        this.numOfRunningTasks = 0;

        if (this.autoStart) {
            this.start();
        }

    }

    // public api:

    /**
     * Stopping the Processing.
     * Notice: active task will not be stopped.
     */
    TasksManager.prototype.stop = function () {
        this.state = QUEUE_STATES.STOPPED;
    };

    /**
     * Starting the Task Manager.
     */
    TasksManager.prototype.start = function () {
        this.state = this.numOfRunningTasks > 0 ? QUEUE_STATES.PROCESSING : QUEUE_STATES.IDLE;
        this._next();
    };

    TasksManager.prototype._getTaskIndex = function(task){
        for(var i=0; i<this.queue.length; i++){
            if(this.queue[i] === task){
                return i;
            }
        }
    };

    TasksManager.prototype.forceStart = function(task){
        if(this.removeTask(task)){
            this._processTask(task);
            return true;
        }
        return false;
    };

    TasksManager.prototype.removeTask = function(task){
        var taskIdx = this._getTaskIndex(task);
        if(this.queue[taskIdx] === task){
            this.queue.splice(taskIdx,1);
            return true;
        }
        return false;
    };

    /**
     * add a task into the queue, of the task manager.
     *
     * @param taskObj
     */
    TasksManager.prototype.addTask = function (taskObj) {
        this.queue.unshift(taskObj);
        this._next();
    };

    // TODO : add array + ordering.


    // TODO: add JSON Notation.

    /**
     * Factory function to create a Task.
     *
     * @param options - factory options:
     * @param options.action - the function to run.
     * @param options.priority - the priority of the task.
     * @param options.timeout - the timeout for this task, if not supplied, default will takeover.
     * @param options.taskStartedCallback - will be raised when the task will start.
     * @param options.taskEndCallback - will be raised when the task will end.
     * @param options.taskErrorCallback - will be raised when the task fails with unexpected Error. - please see notice at doc level.
     * @returns {Task}
     */
    TasksManager.prototype.createTask = function (options) {
        this.logger.debug('creating task', 'createTask');

        if(options && !options.timeout){
            options.timeout = this.taskTimeout;
        }

        return new Task(options);
    };

    TasksManager.prototype.createAndAddTask = function (options) {
        var nTask = this.createTask(options);
        this.addTask(nTask);
        return nTask;
    };


    // private api:
    // -----------
    TasksManager.prototype._next = function () {
        if (this.state !== QUEUE_STATES.STOPPED) {
            var openSlots = this.maxParallelTasks - this.numOfRunningTasks;

            // sort the array: this is happening here to allow dynamic priority change.
            this.queue.sort(function (item1, item2) {
                return item2.priority - item1.priority;
            });

            for (var i = 0; i < openSlots; i++) {
                if (this.queue.length > 0) {
                    var nextTask = this.queue.pop();
                    this._processTask(nextTask);
                }
            }
        }
    };

    TasksManager.prototype._processTask = function (taskObj) {
        this.state = QUEUE_STATES.PROCESSING;
        this.numOfRunningTasks++;

        var doneCallback = this.onTaskComplete.bind(this, taskObj);
        taskObj.start(doneCallback);
    };

    TasksManager.prototype.onTaskComplete = function () {
        this.numOfRunningTasks--;
        this._next();
    };

    // Attaching the CONST'S on the Class.
    TasksManager.QUEUE_STATES = QUEUE_STATES;
    Task.TASK_STATES = TASK_STATES;

    TasksManager.Task = Task;


    return TasksManager;

});
